import { saveRetailerCredentials } from "@/api/rest/checkoutApi";
import { OutlineButton } from "@/components/StoreComponents/StoreButton";
import { truncateString } from "@/utils/StringUtils";
import { TABLET_SCREEN_SIZE } from "@brandclub/common-ui";
import { Box, ButtonBase } from "@mui/material";
import { getEntityImage } from "@stackline/ui";
import { useEffect, useState } from "react";
import { useCheckoutUI } from "../../../CheckoutUIProvider";
import { useCheckoutRetailer } from "../../../hooks/useCheckoutRetailer";
import EditConnectPopup from "./EditConnectPopup";

import { getTOtp, syncConnections } from "@/api";
import { useAppSelector } from "@/redux/hooks";
import CryptoJS from "crypto-js";
import { useCheckoutCartInfo } from "../../../hooks/useCheckoutCartInfo";
import {
  AccountConnectBenefits,
  AccountConnectRewards,
  RewardExpandContainer,
} from "@/components/Checkout/components/CheckoutSummary/components/AccountRewards";

export const useInjectRetailerCredentials = () => {
  const [state] = useCheckoutUI();

  const retailers = useAppSelector((state) => state.retailers);
  const currentRetailer = retailers.find(
    (x) => x.retailerId === state.context.checkoutIntent?.retailerId
  );
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { token, currentStamp } = await getTOtp();
        const accountsDataEncrypted = await syncConnections({
          currentStamp,
          shouldFetchAllConnectionStatuses: true,
        });
        const accounts = JSON.parse(
          CryptoJS.AES.decrypt(accountsDataEncrypted, token).toString(
            CryptoJS.enc.Utf8
          )
        );

        const account = accounts?.find(
          (x: any) => x.retailerId === currentRetailer?.parentRetailerId
        );

        if (account && currentRetailer?.retailerId) {
          setUsername(account.credential.username);
          setPassword(account.credential.password);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentRetailer]);

  return { username, password, loading };
};

export const RetailerConnect = () => {
  const {
    username: existingUsername,
    password: existingPassword,
    loading,
  } = useInjectRetailerCredentials();

  const [state, send] = useCheckoutUI();
  const { currentRetailer } = useCheckoutRetailer();
  const { rewardAmounts } = useCheckoutCartInfo();
  const { checkoutIntent } = state.context;
  const [currentUsername, setCurrentUsername] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState<string>("");

  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    // Set the default password to the decrypted password if it matches the hash
    if (!loading) {
      if (existingUsername) setCurrentUsername(existingUsername);
      if (existingPassword) setCurrentPassword(existingPassword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPassword, existingUsername, loading]);

  const openEdit = () => {
    setShowEdit(true);
  };

  const closeEdit = () => {
    setShowEdit(false);
  };

  const onSaveCredentials = async (username: string, password: string) => {
    setCurrentUsername(username);
    setCurrentPassword(password);
    try {
      if (!currentRetailer?.retailerId) {
        throw new Error("Retailer ID is required");
      }
      const checkoutIntent = await saveRetailerCredentials({
        retailerId: currentRetailer?.retailerId.toString(),
        checkoutIntentId: state.context.checkoutIntent?.checkoutIntentId!,
        credentials: {
          username,
          password,
        },
      });

      send({
        type: "SET_CHECKOUT_INTENT",
        data: { checkoutIntent },
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (!currentRetailer) {
    return null;
  }

  return (
    <div>
      <EditConnectPopup
        showEdit={showEdit}
        closeEdit={closeEdit}
        connectReward={rewardAmounts?.retailerConnectRewardAmount}
        onSaveCredentials={onSaveCredentials}
        currentUsername={currentUsername}
        currentPassword={currentPassword}
        retailer={currentRetailer}
        checkoutIntentUsername={checkoutIntent?.retailerConnection?.username}
      />

      {checkoutIntent?.retailerConnection ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingRight: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                fontSize: 16,
                fontWeight: 500,
                [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
                  fontSize: 14,
                },
              }}
            >
              {truncateString(checkoutIntent?.retailerConnection?.username, 28)}
            </Box>
          </Box>
          <div>
            <ButtonBase
              sx={{
                fontSize: 12,
                fontWeight: 500,
                textDecoration: "underline",
              }}
              onClick={openEdit}
            >
              edit
            </ButtonBase>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <OutlineButton
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              width: "100%",
            }}
            disableMaxWidth
            onClick={openEdit}
          >
            <img
              src={getEntityImage(currentRetailer?.retailerId!, "retailer")}
              alt="Retailer Logo"
              style={{
                width: "24px",
                height: "24px",
              }}
            />
            Connect my {currentRetailer?.displayName} account
          </OutlineButton>

          <RewardExpandContainer>
            <AccountConnectRewards
              retailerId={currentRetailer?.retailerId}
              purchaseAmount={rewardAmounts?.purchaseRewardAmount}
              connectAmount={rewardAmounts?.retailerConnectRewardAmount}
            />
            <AccountConnectBenefits retailerId={currentRetailer?.retailerId} />
          </RewardExpandContainer>
        </div>
      )}
    </div>
  );
};
